import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { EMPTY, Observable } from "rxjs";
import { expand, map, reduce, tap } from "rxjs/operators";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  Member,
  MemberLight,
  MemberPosition,
  PublicMember
} from "../models/member/member";
import { MemberCreate } from "../models/member/member.create";
import { MemberUpdate } from "../models/member/member.update";
import { appState } from "src/app/core/state";
import { alphabetical } from "radash";
import { getCustomerNominative } from "src/app/support/customer";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import { searchListOfObjects } from "src/app/support/search";
import { isUndefined } from "es-toolkit";

const MAX_MEMBER_PAGE_SIZE = 500;

@Injectable()
export class MemberBackend {
  private readonly http = inject(HttpClient);

  @UserNotify({
    successMessage: marker("Membro creato"),
    errorMessage: (errorResponse: HttpErrorResponse) => {
      const { statusCode } = errorResponse.error;

      if (statusCode === 409) {
        return marker("È stato raggiunto il massimo numero di membri");
      }

      return marker("Non è stato possibile creare il nuovo membro");
    }
  })
  create(request: MemberCreate): Observable<string> {
    return this.http.post<string>(
      `${environment.snoots.api.iam}/members`,
      request
    );
  }

  createOnboarding({
    account,
    tenant,
    member
  }: {
    account: string;
    tenant: string;
    member: Omit<Partial<Member>, "roles"> & { roles: string[] };
  }): Observable<Member> {
    return this.http.post<any>(
      `${environment.snoots.api.iam}/accounts/${account}/tenants/${tenant}/members`,
      member
    );
  }

  @UserNotify({
    successMessage: marker("Membro creato"),
    errorMessage: marker("Non è stato possibile creare il nuovo membro")
  })
  createApi(
    tenantId: string,
    request: { name: string; roles: string[] }
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.iam}/tenants/${tenantId}/api`,
      { ...request, type: "api" }
    );
  }

  @UserNotify({
    successMessage: marker("Membro creato"),
    errorMessage: marker("Non è stato possibile creare il nuovo membro")
  })
  createBox(
    tenantId: string,
    request: { name: string; roles: string[] }
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.iam}/tenants/${tenantId}/api`,
      { ...request, type: "box" }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<Member>> {
    let { page, pageSize, filterValues } = request;
    const normalizedPage: number = this.normalizePage(page);
    const normalizedPageSize: number = this.normalizePageSize(pageSize);

    if (!filterValues?.orderBy) {
      filterValues = {
        ...filterValues,
        orderBy: "updatedAt",
        sort: "desc"
      };
    }

    return this.http.get<PaginatedResult<Member>>(
      `${environment.snoots.api.iam}/members`,
      {
        params: omitUndefinedAndNullProperties({
          p: normalizedPage,
          s: normalizedPageSize,
          deleted: false,
          ...filterValues
        })
      }
    );
  }

  search(
    request: PageRequest,
    textFilters: { email?: string; memberName?: string }
  ): Observable<PaginatedResult<Member>> {
    const { page, pageSize } = request;
    const serverFilterValues: Record<string, string | boolean> = {
      ...request.filterValues
    };
    delete serverFilterValues.email;
    delete serverFilterValues.memberName;

    return this.fetchAllListDocs(serverFilterValues).pipe(
      map((members) => {
        let filteredMembers: Member[] = members;

        if (textFilters.email) {
          filteredMembers = searchListOfObjects(
            filteredMembers,
            (member) => member.email ?? "",
            textFilters.email
          );
        }

        if (textFilters.memberName) {
          filteredMembers = searchListOfObjects(
            filteredMembers,
            (member) => getCustomerNominative(member, true),
            textFilters.memberName
          );
        }

        return this.slicePaginatedResult(filteredMembers, page, pageSize);
      })
    );
  }

  listByUserLinked(
    request: PageRequest,
    options: {
      userLinked: boolean;
      textFilters?: { email?: string; memberName?: string };
    }
  ): Observable<PaginatedResult<Member>> {
    const { page, pageSize } = request;
    const serverFilterValues: Record<string, string | boolean> = {
      ...request.filterValues
    };
    delete serverFilterValues.email;
    delete serverFilterValues.memberName;

    return this.fetchAllListDocs(serverFilterValues).pipe(
      map((members) => {
        let filteredMembers: Member[] = members.filter((member) =>
          options.userLinked ? !!member.user : !member.user
        );

        if (options.textFilters?.email) {
          filteredMembers = searchListOfObjects(
            filteredMembers,
            (member) => member.email ?? "",
            options.textFilters.email
          );
        }

        if (options.textFilters?.memberName) {
          filteredMembers = searchListOfObjects(
            filteredMembers,
            (member) => getCustomerNominative(member, true),
            options.textFilters.memberName
          );
        }

        return this.slicePaginatedResult(filteredMembers, page, pageSize);
      })
    );
  }

  listActive(
    request: PageRequest,
    tenant?: string
  ): Observable<PaginatedResult<Member>> {
    const normalizedPage: number = this.normalizePage(request.page);
    const normalizedPageSize: number = this.normalizePageSize(request.pageSize);
    const resolvedTenant: string | undefined = this.resolveTenantId(tenant);

    return this.http
      .get<PaginatedResult<Member>>(`${environment.snoots.api.iam}/members`, {
        params: omitUndefinedAndNullProperties({
          p: normalizedPage,
          s: normalizedPageSize,
          active: true,
          deleted: false,
          tenant: resolvedTenant,
          ...request.filterValues
        })
      })
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: alphabetical(response.docs, (member) =>
              getCustomerNominative(member)
            )
          };
        })
      );
  }

  numberOfActive(
    request: PageRequest
  ): Observable<PaginatedResult<{ subscribedMembers: number }>> {
    return this.http
      .get<{
        subscribedMembers: number;
      }>(`${environment.snoots.api.notification}/stats`)
      .pipe(
        map((result) => {
          return {
            docs: [{ subscribedMembers: result.subscribedMembers || 0 }],
            hasNextPage: false,
            hasPrevPage: false,
            limit: 0,
            nextPage: 1,
            page: 1,
            pagingCounter: 1,
            totalDocs: result.subscribedMembers,
            totalPages: result.subscribedMembers
          } as PaginatedResult<{ subscribedMembers: number }>;
        })
      );
  }

  listAllLight(tenant?: string): Observable<MemberLight[]> {
    return this.fetchAllMemberLightPages(
      { deleted: false, light: true },
      tenant
    ).pipe(map((docs) => alphabetical(docs, (x) => getCustomerNominative(x))));
  }

  listActiveLight(
    request: PageRequest,
    tenant?: string
  ): Observable<PaginatedResult<MemberLight>> {
    const normalizedPage: number = this.normalizePage(request.page);
    const normalizedPageSize: number = this.normalizePageSize(request.pageSize);
    const resolvedTenant: string | undefined = this.resolveTenantId(tenant);

    return this.http
      .get<PaginatedResult<MemberLight>>(
        `${environment.snoots.api.iam}/members`,
        {
          params: omitUndefinedAndNullProperties({
            p: normalizedPage,
            s: normalizedPageSize,
            active: true,
            deleted: false,
            light: true,
            tenant: resolvedTenant,
            ...request.filterValues
          })
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: alphabetical(response.docs, (member) =>
              getCustomerNominative(member)
            )
          };
        })
      );
  }

  listActiveAll(tenant?: string): Observable<Member[]> {
    const resolvedTenant: string | undefined = this.resolveTenantId(tenant);

    return this.fetchAllActiveMemberDocs(resolvedTenant, {
      type: "human",
      system: "false",
      disabled: "false"
    }).pipe(map((docs) => docs));
  }

  listActiveAllLight(
    orderInverted: boolean = false,
    tenant?: string,
    options?: {
      noSystem?: boolean;
    }
  ): Observable<MemberLight[]> {
    if (isUndefined(options)) {
      options = {
        noSystem: true
      };
    } else {
      if (isUndefined(options.noSystem)) {
        options.noSystem = true;
      }
    }

    const resolvedTenant: string | undefined = this.resolveTenantId(tenant);

    return this.fetchAllActiveLightDocs(resolvedTenant, {
      type: "human",
      system: options?.noSystem ? "false" : undefined,
      disabled: "false"
    }).pipe(
      map((docs) =>
        alphabetical(docs, (member) =>
          getCustomerNominative(member, orderInverted)
        )
      ),
      tap((members: MemberLight[]) => {
        appState.members = members;
      })
    );
  }

  listActiveAllByPositionLight(
    position?: MemberPosition,
    tenant?: string,
    options?: {
      noSystem?: boolean;
    }
  ): Observable<MemberLight[]> {
    const resolvedTenant: string | undefined = this.resolveTenantId(tenant);

    return this.fetchAllActiveLightDocs(resolvedTenant, {
      type: "human",
      system: options?.noSystem ? "false" : undefined,
      disabled: "false",
      position
    }).pipe(
      map((docs) =>
        alphabetical(docs, (member) => getCustomerNominative(member, true))
      ),
      tap((members: MemberLight[]) => {
        appState.members = members;
      })
    );
  }

  loadByEmail(
    email: string,
    accountId: string
  ): Observable<PaginatedResult<Member>> {
    return this.http.get<PaginatedResult<Member>>(
      `${environment.snoots.api.iam}/accounts/${accountId}/members`,
      {
        params: {
          email
        }
      }
    );
  }

  load(id: string): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/${id}`);
  }

  loadPublic(id: string): Observable<PublicMember> {
    return this.http.get<PublicMember>(
      `${environment.snoots.api.iam}/public/members/${id}`
    );
  }

  loadLight(id: string): Observable<MemberLight> {
    return this.http.get<MemberLight>(
      `${environment.snoots.api.iam}/members/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  loadMe(): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/me`);
  }

  loadMeLight(): Observable<MemberLight> {
    return this.http.get<MemberLight>(
      `${environment.snoots.api.iam}/members/me`,
      {
        params: {
          light: true
        }
      }
    );
  }

  loadMeWithToken({
    memberToken
  }: {
    memberToken: string;
  }): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/me`, {
      headers: {
        Member: memberToken
      }
    });
  }

  loadForAnalytics(id: string): Observable<{ id: string; name: string }> {
    return this.http
      .get<Member>(`${environment.snoots.api.iam}/members/${id}`)
      .pipe(
        map((member) => {
          return {
            ...member,
            name: `${member.firstName} ${member.lastName}`
          };
        })
      );
  }

  @UserNotify({
    successMessage: marker("Membro cancellato"),
    errorMessage: marker(
      "Non è stato possibile cancellare il membro selezionato"
    )
  })
  delete(id: string): Observable<string> {
    return this.http.delete<string>(
      `${environment.snoots.api.iam}/members/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Membro aggiornato"),
    errorMessage: marker(
      "Non è stato possibile aggiornare il membro selezionato"
    )
  })
  update(id: string, request: Partial<MemberUpdate>): Observable<string> {
    return this.http.patch<string>(
      `${environment.snoots.api.iam}/members/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Membro aggiornato"),
    errorMessage: marker(
      "Non è stato possibile aggiornare il membro selezionato"
    )
  })
  updateMe(request: Partial<MemberUpdate>): Observable<string> {
    return this.http.patch<string>(
      `${environment.snoots.api.iam}/members/me`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Invito rispedito"),
    errorMessage: marker("Non è stato possibile rispedire l'invito")
  })
  sendInvite(id: string): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.iam}/members/${id}/invite`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Utente disabilitato"),
    errorMessage: marker("Non è stato possibile disabilitare l'utente")
  })
  disable(id: string): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.iam}/members/${id}/disable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Utente abilitato"),
    errorMessage: marker("Non è stato possibile abilitare l'utente")
  })
  enable(id: string): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.iam}/members/${id}/enable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Password cambiata"),
    errorMessage: marker("Non è stato possibile cambiarae la password")
  })
  resetPassword(id: string): Observable<{ password: string }> {
    return this.http.put<{ password: string }>(
      `${environment.snoots.api.iam}/apis/${id}/reset-password`,
      {}
    );
  }

  private normalizePage(page: number | undefined): number {
    if (page === undefined || page === null || page < 1) {
      return 1;
    }

    return page;
  }

  private normalizePageSize(pageSize: number | undefined): number {
    if (pageSize === undefined || pageSize === null || pageSize < 1) {
      return MAX_MEMBER_PAGE_SIZE;
    }

    return Math.min(pageSize, MAX_MEMBER_PAGE_SIZE);
  }

  private resolveTenantId(tenant?: string): string | undefined {
    return tenant ?? appState.tenantId;
  }

  private fetchAllListDocs(
    filterValues: Record<string, string | boolean>
  ): Observable<Member[]> {
    const loadPage = (page: number): Observable<PaginatedResult<Member>> =>
      this.list({ page, pageSize: MAX_MEMBER_PAGE_SIZE, filterValues });

    return loadPage(1).pipe(
      expand((result) =>
        result.hasNextPage ? loadPage((result.page ?? 1) + 1) : EMPTY
      ),
      map((result) => result.docs),
      reduce((accumulator, docs) => accumulator.concat(docs), [] as Member[])
    );
  }

  private fetchAllActiveMemberDocs(
    tenant: string | undefined,
    filterValues: Record<string, string | boolean | undefined>
  ): Observable<Member[]> {
    const loadPage = (page: number): Observable<PaginatedResult<Member>> =>
      this.listActive(
        {
          page,
          pageSize: MAX_MEMBER_PAGE_SIZE,
          filterValues: omitUndefinedAndNullProperties(filterValues)
        },
        tenant
      );

    return loadPage(1).pipe(
      expand((result) =>
        result.hasNextPage ? loadPage((result.page ?? 1) + 1) : EMPTY
      ),
      map((result) => result.docs),
      reduce((accumulator, docs) => accumulator.concat(docs), [] as Member[])
    );
  }

  private fetchAllActiveLightDocs(
    tenant: string | undefined,
    filterValues: Record<string, string | boolean | MemberPosition | undefined>
  ): Observable<MemberLight[]> {
    const loadPage = (page: number): Observable<PaginatedResult<MemberLight>> =>
      this.listActiveLight(
        {
          page,
          pageSize: MAX_MEMBER_PAGE_SIZE,
          filterValues: omitUndefinedAndNullProperties(filterValues)
        },
        tenant
      );

    return loadPage(1).pipe(
      expand((result) =>
        result.hasNextPage ? loadPage((result.page ?? 1) + 1) : EMPTY
      ),
      map((result) => result.docs),
      reduce(
        (accumulator, docs) => accumulator.concat(docs),
        [] as MemberLight[]
      )
    );
  }

  private fetchAllMemberLightPages(
    queryParams: Record<string, string | boolean>,
    tenant?: string
  ): Observable<MemberLight[]> {
    const resolvedTenant: string | undefined = this.resolveTenantId(tenant);
    const loadPage = (page: number): Observable<PaginatedResult<MemberLight>> =>
      this.http.get<PaginatedResult<MemberLight>>(
        `${environment.snoots.api.iam}/members`,
        {
          params: omitUndefinedAndNullProperties({
            p: page,
            s: MAX_MEMBER_PAGE_SIZE,
            tenant: resolvedTenant,
            ...queryParams
          })
        }
      );

    return loadPage(1).pipe(
      expand((result) =>
        result.hasNextPage ? loadPage((result.page ?? 1) + 1) : EMPTY
      ),
      map((result) => result.docs),
      reduce(
        (accumulator, docs) => accumulator.concat(docs),
        [] as MemberLight[]
      )
    );
  }

  private slicePaginatedResult<T>(
    filteredDocs: T[],
    page: number | undefined,
    pageSize: number | undefined
  ): PaginatedResult<T> {
    const normalizedPage: number = this.normalizePage(page);
    const normalizedPageSize: number = this.normalizePageSize(pageSize);
    const totalDocs: number = filteredDocs.length;
    const totalPages: number = Math.max(
      Math.ceil(totalDocs / normalizedPageSize),
      1
    );
    const start: number = (normalizedPage - 1) * normalizedPageSize;
    const docs: T[] = filteredDocs.slice(start, start + normalizedPageSize);

    return {
      docs,
      totalDocs,
      totalPages,
      page: normalizedPage,
      limit: normalizedPageSize,
      hasNextPage: normalizedPage < totalPages,
      hasPrevPage: normalizedPage > 1,
      nextPage:
        normalizedPage < totalPages ? normalizedPage + 1 : normalizedPage,
      pagingCounter: start + 1,
      prevPage: null
    } as PaginatedResult<T>;
  }
}
